import type { Boundary } from "@floating-ui/dom";
import { PluginKey } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";
import type { BubbleMenuProps } from "@tiptap/react/menus";
import { useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

type BubbleMenuPlacement = "top" | "bottom";

const OVERFLOW_PADDING = 8;

// Old Tippy gap: its 10px distance, with the `text-modifiers` theme nudging the
// box 4px down (6px above the selection, 14px below).
const TIPPY_DISTANCE = 10;
const TIPPY_THEME_NUDGE = 4;

// Tippy's `shift-away-subtle` enter animation.
const SHOW_ANIMATION_DURATION = 200;
const SHOW_ANIMATION_DISTANCE = 5;

const getSelectionRect = (editor: Editor): DOMRect => {
  try {
    const selection = editor.view.dom.ownerDocument.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const rects = selection.getRangeAt(0).getClientRects();
      const lastRect = rects.item(rects.length - 1);
      if (lastRect) {
        return new DOMRect(
          lastRect.left,
          lastRect.top,
          Math.max(lastRect.width, 1),
          Math.max(lastRect.height, 1),
        );
      }
    }

    const { from, to } = editor.state.selection;
    const start = editor.view.coordsAtPos(from);
    const end = editor.view.coordsAtPos(to);
    const top = Math.min(start.top, end.top);
    const bottom = Math.max(start.bottom, end.bottom);
    const left = Math.min(start.left, end.left);
    const right = Math.max(start.right, end.right);
    return new DOMRect(
      left,
      top,
      Math.max(right - left, 1),
      Math.max(bottom - top, 1),
    );
  } catch {
    return new DOMRect(0, 0, 0, 0);
  }
};

// Keeps the menu attached to the selection when a modal or other container
// scrolls. TipTap only listens for `window` scroll; capturing on `document`
// also catches scrolls inside elements.
const followScroll = (editor: Editor, pluginKey: PluginKey) => {
  const onScroll = () => {
    if (editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta(pluginKey, "updatePosition"));
  };
  document.addEventListener("scroll", onScroll, {
    capture: true,
    passive: true,
  });
  return () =>
    document.removeEventListener("scroll", onScroll, { capture: true });
};

export const useBubbleMenuOptions = (
  placement: BubbleMenuPlacement = "top",
  allowBubbleMenuOverflow = true,
  editor?: Editor,
): Pick<
  BubbleMenuProps,
  "getReferencedVirtualElement" | "options" | "pluginKey"
> & {
  ref: RefObject<HTMLDivElement | null>;
} => {
  const ref = useRef<HTMLDivElement>(null);
  const [pluginKey] = useState(() => new PluginKey("bubbleMenu"));

  return useMemo(() => {
    let stopFollowingScroll: (() => void) | undefined;

    const getBoundary = (): Boundary =>
      allowBubbleMenuOverflow ? "clippingAncestors" : [];

    return {
      ref,
      pluginKey,
      getReferencedVirtualElement: editor
        ? () => ({
            getBoundingClientRect: () => getSelectionRect(editor),
            contextElement: editor.view.dom,
          })
        : undefined,
      options: {
        strategy: "fixed" as const,
        // Floating UI resolves the offset parent from the menu's current CSS
        // position, which TipTap leaves as `absolute` until after the first
        // calculation.
        onShow: () => {
          if (editor) stopFollowingScroll = followScroll(editor, pluginKey);
          if (!ref.current) return;
          ref.current.style.position = "fixed";

          const startY =
            placement === "top"
              ? SHOW_ANIMATION_DISTANCE
              : -SHOW_ANIMATION_DISTANCE;
          ref.current.animate?.(
            [
              { opacity: 0, transform: `translateY(${startY}px)` },
              { opacity: 1, transform: "none" },
            ],
            { duration: SHOW_ANIMATION_DURATION, easing: "ease" },
          );
        },
        onHide: () => stopFollowingScroll?.(),
        placement,
        // Uses the resolved placement so a flipped menu keeps the same offset as Tippy
        offset: ({ placement: resolvedPlacement }) =>
          resolvedPlacement.startsWith("top")
            ? TIPPY_DISTANCE - TIPPY_THEME_NUDGE
            : TIPPY_DISTANCE + TIPPY_THEME_NUDGE,
        flip: () => ({
          boundary: getBoundary(),
          altBoundary: true,
          fallbackPlacements: [placement === "top" ? "bottom" : "top"],
          padding: OVERFLOW_PADDING,
        }),
        shift: () => ({
          boundary: getBoundary(),
          altBoundary: true,
          padding: OVERFLOW_PADDING,
          // Keep the menu inside the selection's scroll container on both axes.
          crossAxis: true,
        }),
      },
    };
  }, [placement, allowBubbleMenuOverflow, editor, pluginKey]);
};
